/**
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */
import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import { ReactQueryDevtools } from 'react-query/devtools';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import { CircularProgress } from '@material-ui/core';
import CssBaseline from '@material-ui/core/CssBaseline';
import { createStyles, MuiThemeProvider, withStyles } from '@material-ui/core/styles';
import withWidth, { WithWidth } from '@material-ui/core/withWidth';
import { ErrorBoundaryApp, HeaderGap, InternetExplorerAlert, LoadingProgress, Snackbar } from 'core';
import { getTheme } from 'core/styles/getTheme';
import { get, includes } from 'lodash';

import { changeLocale, changeLocation } from '@lib/actions';
import initApp from '@lib/actions/initApp';
import { useCheckSessionExpired, useCurrentLanguage } from '@lib/hooks';
import { languages, Locale } from '@lib/providers/LanguageProvider';
import { Language } from '@lib/resources/types';
import { selectAppReady, selectCurrentUser, selectCustomThemeProps, selectMessage } from '@lib/selectors';
import configService from '@lib/services/configService';
import { isIE } from '@lib/services/navigatorService';
import storage from '@lib/services/storageService';

import TranslateBanner from 'components/TranslateBanner';
import Header, { useHeaderBrandingConfiguration } from 'containers/Header';

import { LS_DO_NOT_SHOW_LANGUAGE_BANNER } from './constants';
import { makeSelectError, makeSelectLoading } from './selectors';
import useDatadogIdentify from './useDatadogIdentify';
import useDatadogStartSessionReplayRecording from './useDatadogStartSessionReplayRecording';
import useFullStoryIdentify from './useFullStoryIdentify';
import useInjectOneTrustScript from './useInjectOneTrustScript';
import useRequireAuthentication from './useRequireAuthentication';

const styles = () =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'auto',
    },
    errorBoundary: {
      position: 'absolute',
    },
    loadingWrapper: {
      position: 'absolute',
      inset: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  });

const sessionExpireMinutes = configService.getSessionExpireMinutes();

interface Props extends WithWidth, RouteComponentProps<any, any> {
  classes: any;
}

const App: React.FC<Props> = ({ classes, children, location, width }) => {
  const dispatch = useDispatch();
  const { pathname } = location;
  const { needsAuthentication, redirectToLoginPage } = useRequireAuthentication(pathname);
  const appReady = useSelector(selectAppReady);
  const currentUser = useSelector(selectCurrentUser);
  const currentLanguage = useCurrentLanguage();
  const themeProps = useSelector(selectCustomThemeProps);
  const message = useSelector(selectMessage);
  const loading = useSelector(makeSelectLoading());
  const error = useSelector(makeSelectError());
  const isDesktop = /md|lg|xl/.test(width);
  const {
    includeLanguageSelector,
    includeBarWithLinks,
    includeButtonWithLinks,
    links,
  } = useHeaderBrandingConfiguration(isDesktop);
  const isWidgetScreen = /lumabot/.test(pathname);
  useInjectOneTrustScript();
  useCheckSessionExpired(sessionExpireMinutes, '/session-expired', !/lumabot/.test(pathname));
  useDatadogStartSessionReplayRecording();
  useDatadogIdentify();
  useFullStoryIdentify();

  useEffect(() => {
    if (!appReady) {
      dispatch(changeLocation(location));
      dispatch(initApp.request({ location }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (needsAuthentication) {
      redirectToLoginPage();
    }
  }, [currentUser, needsAuthentication, redirectToLoginPage]);

  const showLoading = (!isWidgetScreen && !appReady && !pathname.includes('/login')) || needsAuthentication;

  // prevent blank screen on login page
  if (showLoading) {
    return (
      <div className={classes.loadingWrapper}>
        <CircularProgress />
      </div>
    );
  }

  if (isIE) return <InternetExplorerAlert />;

  const muiTheme = getTheme(themeProps);
  const currentUserLanguage = (currentUser && currentUser.language) || Language.English;

  const lumaAppThemeProps = get(muiTheme, 'props.LumaApp');
  const includeHeader = !/telehealth/.test(pathname) && !isWidgetScreen;

  return (
    <MuiThemeProvider theme={muiTheme}>
      {!isWidgetScreen && <CssBaseline />}
      <ErrorBoundaryApp className={classes.errorBoundary}>
        {/*
      // @ts-ignore */}
        <Helmet
          titleTemplate="%s - Luma Health"
          defaultTitle={lumaAppThemeProps.pageTitle}
          link={[
            {
              rel: 'icon',
              type: 'image/png',
              href: lumaAppThemeProps.favicon,
            },
          ]}
          meta={[{ name: 'description', content: 'A React.js application for Luma Health' }]}
        />
        {includeHeader && (
          <Header
            includeActions
            includeLanguageSelector={includeLanguageSelector}
            includeBarWithLinks={includeBarWithLinks}
            includeButtonWithLinks={includeButtonWithLinks}
            links={links}
          />
        )}
        <div className={classes.root}>
          {includeHeader && <HeaderGap includeBarWithLinks={includeBarWithLinks} />}
          {!isWidgetScreen && (
            <TranslateBanner
              language={currentUserLanguage}
              onChange={(l: Locale) => dispatch(changeLocale(l))}
              show={
                !storage.get(LS_DO_NOT_SHOW_LANGUAGE_BANNER) &&
                currentLanguage !== currentUserLanguage &&
                includes(languages, currentUserLanguage)
              }
            />
          )}
          <ErrorBoundaryApp>{React.Children.toArray(children)}</ErrorBoundaryApp>
          {!isWidgetScreen && (
            <>
              <LoadingProgress loading={loading} />
              {/* TODO: reuse message reducer to show errors */}
              <Snackbar content={error || message} variant={message.variant || 'error'} />
            </>
          )}
        </div>
      </ErrorBoundaryApp>
      {`${process.env.REACT_APP_SKIP_REACT_QUERY_DEV_TOOLS}` !== 'true' && <ReactQueryDevtools initialIsOpen={false} />}
    </MuiThemeProvider>
  );
};

export default withWidth()(withStyles(styles, { name: 'LumaApp' })(App));
